import { useEffect, useState, useCallback } from "react";
import { Plus, Search, Package, MoreVertical, Pencil, Trash2, ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatRupiah, formatApiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ConfirmDialog from "@/components/ConfirmDialog";
import DataPagination from "@/components/DataPagination";
import { TableSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const empty = { name: "", type: "product", category: "", cost_price: 0, sell_price: 0, description: "", image: null, active: true };
const CATEGORY_SUGGEST = ["Jasa Mikrotik", "VPN", "Instalasi", "Maintenance", "Produk Jaringan"];

const Products = () => {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: { search, type, status, page, limit: 8 } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [search, type, status, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };

  const onImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Ukuran gambar maks 1.5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name) { toast.error("Nama wajib diisi"); return; }
    setSaving(true);
    try {
      const payload = { ...form, cost_price: Number(form.cost_price), sell_price: Number(form.sell_price) };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Produk diperbarui" : "Produk ditambahkan");
      setOpen(false);
      if (!editing) setPage(1);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/products/${delId}`);
      toast.success("Produk dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDelId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Produk & Jasa" subtitle="Kelola katalog produk dan layanan">
        <Button onClick={openNew} data-testid="add-product-btn">
          <Plus className="mr-2 h-4 w-4" /> Tambah
        </Button>
      </PageHeader>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Cari produk/jasa..."
            className="pl-9"
            value={search}
            data-testid="product-search-input"
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <Select value={type} onValueChange={(v) => { setType(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Tipe</SelectItem>
            <SelectItem value="product">Produk</SelectItem>
            <SelectItem value="service">Jasa</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="active">Aktif</SelectItem>
            <SelectItem value="inactive">Nonaktif</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border bg-card p-2 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
        {loading ? (
          <div className="p-4"><TableSkeleton cols={6} /></div>
        ) : data.items.length === 0 ? (
          <EmptyState icon={Package} title="Belum ada produk/jasa" description="Mulai tambahkan produk atau layanan usaha Anda." action={<Button onClick={openNew}><Plus className="mr-2 h-4 w-4" />Tambah Produk</Button>} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produk</TableHead>
                <TableHead>Kode</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead className="text-right">Harga Modal</TableHead>
                <TableHead className="text-right">Harga Jual</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((p) => (
                <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-secondary">
                        {p.image ? <img src={p.image} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-4 w-4 text-muted-foreground" />}
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{p.name}</p>
                        <p className="text-xs capitalize text-muted-foreground">{p.type === "service" ? "Jasa" : "Produk"}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{p.code}</TableCell>
                  <TableCell>{p.category || "-"}</TableCell>
                  <TableCell className="text-right">{formatRupiah(p.cost_price)}</TableCell>
                  <TableCell className="text-right font-medium">{formatRupiah(p.sell_price)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex h-2 w-2 rounded-full ${p.active ? "bg-emerald-500" : "bg-slate-300"} mr-2`} />
                    {p.active ? "Aktif" : "Nonaktif"}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`product-actions-${p.id}`}><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDelId(p.id)} className="text-destructive" data-testid={`delete-product-${p.id}`}><Trash2 className="mr-2 h-4 w-4" />Hapus</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {!loading && <DataPagination page={page} limit={8} total={data.total} onPage={setPage} />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit Produk/Jasa" : "Tambah Produk/Jasa"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-border bg-secondary">
                {form.image ? <img src={form.image} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
              </div>
              <div>
                <Label htmlFor="img" className="cursor-pointer text-sm font-medium text-primary hover:underline">Upload Gambar</Label>
                <input id="img" type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="product-image-input" />
                <p className="text-xs text-muted-foreground">PNG/JPG maks 1.5MB</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-2">
                <Label>Nama *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Tipe</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">Produk</SelectItem>
                    <SelectItem value="service">Jasa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Kategori</Label>
                <Input list="cats" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-category-input" />
                <datalist id="cats">{CATEGORY_SUGGEST.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              <div className="space-y-2">
                <Label>Harga Modal</Label>
                <Input type="number" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} data-testid="product-cost-input" />
              </div>
              <div className="space-y-2">
                <Label>Harga Jual</Label>
                <Input type="number" value={form.sell_price} onChange={(e) => setForm({ ...form, sell_price: e.target.value })} data-testid="product-price-input" />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Deskripsi</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
              <div className="col-span-2 flex items-center justify-between rounded-lg border border-border p-3">
                <Label>Status Aktif</Label>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="product-active-switch" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="save-product-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!delId} onOpenChange={() => setDelId(null)} title="Hapus Produk?" description="Tindakan ini tidak dapat dibatalkan." onConfirm={doDelete} />
    </div>
  );
};

export default Products;
